const en = {
  common: {
    appName: "VytaliDays", loading: "Loading…", save: "Save", cancel: "Cancel", delete: "Delete",
    edit: "Edit", create: "Create", close: "Close", confirm: "Confirm", back: "Back",
    search: "Search", none: "None", yes: "Yes", no: "No", reason: "Reason", submit: "Submit",
    actions: "Actions", points: "points", optional: "optional", language: "Language",
    logout: "Log out", saved: "Saved", required: "Required", share: "Share", start: "Start",
    end: "End", copyLink: "Copy link", linkCopied: "Link copied", sending: "Sending…",
  },
  auth: {
    signIn: "Sign in", email: "Email", password: "Password",
    tagline: "Ambassador activation platform", inviteOnly: "Invite-only access",
    welcome: "Welcome back", acceptTitle: "Activate your account",
    choosePassword: "Choose a password", passwordHint: "8 characters minimum",
    consent: "I agree that photos taken during VytaliDays may be used by Vytali for activations.",
    activate: "Activate my account", invalidInvite: "Invitation invalid or expired",
  },
  nav: {
    home: "Home", events: "Events", checkin: "Check-in", field: "Field", profile: "Profile",
    notifications: "Notifications", dashboard: "Dashboard", users: "Users", teams: "Teams",
    campaigns: "Campaigns", partners: "Partners", review: "Review", reports: "Reports",
    points: "Points", ledger: "Ledger", leads: "Partner requests", system: "System",
    analytics: "Analytics", myQr: "My QR", outbox: "Event leads",
  },
  qr: {
    title: "My personal QR",
    lead: "Show this code during a VytaliDay: every lead captured is automatically attributed to you.",
    download: "Download", attribution: "Automatic attribution",
    autoAttributed: "Event, partner and campaign attributed automatically.",
    noActiveEvent: "No active event. Check in to enable automatic attribution — otherwise the visitor can pick the event.",
    myLeads: "Leads I captured", noLeads: "No leads yet.",
    manualTitle: "Manual lead",
    manualCta: "Add a lead manually",
    manualHint: "For someone without a smartphone or who cannot scan the QR. Same attribution and same handoff to VytaliLeads.",
    manualConsent: "This person consents to being contacted by Vytali about their request.",
    manualSubmit: "Save the lead", manualSaved: "Lead saved and handed off",
    offlineNote: "You are offline: the lead will be stored locally and handed off as soon as you are back online.",
    offlineQueued: "Lead saved — waiting for synchronization",
    offlinePending: "Waiting for synchronization", offlineWaiting: "Waiting for synchronization",
    offlineSyncNow: "Synchronize now", offlineSynced: "{{n}} lead(s) synchronized",
    offlineNeedsEvent: "Pick the event to attribute this lead before synchronizing.",
    offlineSyncNoEvent: "Synchronize without attribution", offlineError: "Failed: {{msg}}",
  },
  capture: {
    eyebrow: "VytaliDays", title: "Let's stay in touch",
    withAmbassador: "With {{name}}",
    firstName: "First name", lastName: "Last name", phone: "Phone", email: "Email",
    business: "Business / company (optional)", interest: "What you are interested in",
    notes: "Notes (optional)", chooseEvent: "Event", noEvent: "No event",
    consent: "I agree to be contacted by Vytali about my request.",
    submit: "Send my details",
    nameRequired: "First name is required",
    contactRequired: "An email or a phone number is required",
    consentRequired: "Your consent is required",
    thanksTitle: "Thank you!", thanksText: "Your details have been sent. A Vytali team member will contact you shortly.",
    invalid: "This QR code is not valid.",
    privacy: "Your data is used for this follow-up only.",
  },
  outbox: {
    title: "Event leads", lead: "Handoff queue for leads captured at events, on their way to VytaliLeads.",
    flush: "Send now", export: "CSV export (fallback)", retry: "Retry",
    retried: "Retry queued", flushed: "{{n}} delivery(ies) processed",
    notConfigured: "VytaliLeads integration not configured",
    notConfiguredHint: "The VytaliLeads API is not connected yet: leads stay queued and will be transmitted as soon as the URL and key are configured.",
    contact: "Contact", attribution: "Attribution", delivery: "Delivery",
    environment: "Environment",
    blocked: "Delivery blocked by configuration: {{reason}}",
    externalId: "VytaliLeads ID", attempts: "Attempts",
    attemptsTotal: "Attempts (auto + manual)", manual: "manual",
    status: { pending: "Pending", sent: "Sent", confirmed: "Confirmed",
              failed: "Failed", retry: "Retry" },
  },
  fieldLeads: {
    title: "Field leads", export: "Export leads", total: "Total",
    qr: "QR self-service", manual: "Manual", offline: "Offline synced",
    pending: "Pending sync", confirmed: "Confirmed in VytaliLeads",
    exported: "Export generated", forbidden: "These leads are outside your scope.",
  },
  report: {
    title: "Activation report", pdf: "Download PDF", reviewSend: "Review & send",
    reviewSendHint: "Reports are never sent automatically. Check the numbers before sending it to the partner.",
    confirmSend: "Send to partner", messageOptional: "Covering message (optional)",
    sent: "Report sent", ambassadors: "Ambassadors mobilised", leads: "Leads generated",
    missions: "Missions completed", content: "Content / proofs", summary: "Results summary",
    leadsDelivery: "Handoff to VytaliLeads",
    lastSend: "Last sent to {{email}} on {{date}}",
    forbidden: "This report is outside your scope.",
    loadError: "The report could not be loaded.",
    quotaExceeded: "Resend daily sending quota reached. Try again tomorrow or raise your Resend limit. The PDF is still downloadable.",
    lastAttemptFailed: "Last send attempt to {{email}} on {{date}} — not delivered",
  },
  retention: {
    title: "Photo retention",
    lead: "Proof photos are deleted automatically after {{days}} days.",
    stored: "Stored", expired: "Expired", purgedTotal: "Purged", days: "days",
    purgeNow: "Purge now", purged: "{{n}} photo(s) deleted",
    purgeErrors: "{{n}} photo(s) could not be purged — check the logs",
    dailyJob: "An automatic purge runs every day. Mission and audit metadata is always preserved.",
  },
  analytics: {
    title: "Analytics", lead: "Performance by event, team, Ambassador and partner.",
    all: "All", reset: "Reset", export: "Export CSV", events: "Events",
    rsvps: "RSVPs", attendanceRate: "Attendance rate", noShowRate: "No-show rate",
    approvedProofs: "Approved proofs", activeAmbassadors: "Active Ambassadors",
    uniqueAmbassadors: "unique Ambassadors", trend: "Attendance by month",
    topAmbassadors: "Top Ambassadors", viewHistory: "History",
    participationHistory: "Participation history",
    event: "Event", recordedAt: "Recorded",
    by: { events: "Event", ambassadors: "Ambassador", teams: "Team",
          partners: "Partner", campaigns: "Campaign" },
  },
  system: {
    activeSupers: "Active super admins", roleAudit: "Role audit log",
    activity: "Activity log", nominate: "Nominate a super admin",
    nominateLead: "Pick a trusted admin so you never lose access.",
    grant: "Promote", revoke: "Demote", supers: "Super admins",
    leadStatus: { new: "New", contacted: "Contacted", qualified: "Qualified", closed: "Closed" },
  },
  account: {
    changeTitle: "Choose your password", changeLead: "To secure your account, set your own password before continuing.",
    currentPassword: "Current password", newPassword: "New password",
    confirmPassword: "Confirm password", savePassword: "Save password",
    changed: "Password updated", tooShort: "8 characters minimum",
    mismatch: "Passwords do not match",
    forgotTitle: "Forgot your password?", forgotLead: "We'll send you a secure reset link.",
    sendResetLink: "Send the link", resetSent: "If an account exists for this address, an email has been sent.",
    resetTitle: "New password", resetInvalid: "Link invalid or expired",
  },
  home: {
    hello: "Hello", nextEvent: "Your next VytaliDay", noEvent: "No upcoming VytaliDay",
    noEventHint: "You'll be notified as soon as an activation is scheduled.",
    checkInNow: "Check in now", myMissions: "My missions", progress: "My progress",
    attended: "Attended", missions: "Missions approved", score: "Score", pending: "Pending",
    viewEvent: "View event", seeAll: "See all",
  },
  events: {
    upcoming: "Upcoming", past: "Past", detail: "Event detail", agenda: "Agenda",
    objectives: "Objectives", partner: "Partner", campaign: "Campaign", location: "Location",
    capacity: "Capacity", missions: "Missions", participants: "Participants",
    rsvp: "Confirm my attendance", cancelRsvp: "Cancel my attendance", waitlisted: "Waitlist",
    rsvpConfirmed: "Attendance confirmed", full: "Full", shortCode: "Short code",
    consentRequired: "Consent required before confirming", noEvents: "No events",
    eventUnavailable: "This event is not available for you right now.",
    confirmedCount: "confirmed", checkedIn: "checked in",
  },
  status: {
    confirmed: "Confirmed", waitlisted: "Waitlisted", cancelled: "Cancelled",
    no_show: "No-show", pending: "Pending", approved: "Approved", rejected: "Rejected",
    draft: "Draft", published: "Published", checked_in: "Checked in", new: "New",
  },
  checkin: {
    title: "Check-in", scan: "Scan QR code", enterCode: "Enter short code",
    code: "Event code", validate: "Validate", success: "Check-in recorded!",
    already: "You are already checked in", offlineQueued: "Offline — saved, sync pending",
    pendingSync: "check-in awaiting sync", syncNow: "Sync now",
    pendingSyncNotice: "Check-in pending sync: proof submission unlocks as soon as you are back online.",
    stateConfirmed: "Attendance confirmed", statePendingSync: "Check-in pending sync",
    stateNone: "No check-in yet",
    scannerMock: "Camera unavailable? Use the short code.",
  },
  missions: {
    submitProof: "Submit proof", photo: "Photo", note: "Note", completion: "Mark complete",
    reminder: "Reminder: event-related content only. Avoid identifiable people without their consent.",
    takePhoto: "Choose a photo", noteLabel: "Your note", submitted: "Proof submitted — pending review",
    resubmit: "Submit again", myProof: "My proof", proofRemoved: "Photo removed by an administrator",
    objective: "Objective", instructions: "Instructions", proofExpected: "Expected proof",
    deadline: "Deadline", required: "Required", optionalMission: "Optional",
    viewDetail: "View details", openAndSubmit: "View details and submit",
    checkinFirst: "Check in on site to be able to submit your proof.",
    expected_photo: "A photo taken during the event.",
    expected_note: "A written note describing what you achieved.",
    expected_completion: "A simple confirmation that the mission is done.",
  },
  progress: {
    title: "My progress", completed: "{{done}} / {{total}} missions completed",
    pointsEarned: "Points earned", missionPoints: "{{n}} pts missions", attendancePoints: "{{n}} pts attendance",
    potential: "Pending", potentialHint: "Not counted until approved", todo: "To do",
  },
  field: {
    title: "Field Mode", subtitle: "Everything you need during the activation.",
    doCheckin: "Check me in", captureLead: "Capture a lead",
    showQr: "Show my QR", addManual: "Add manually",
    myMissions: "My missions", offlineMode: "Offline mode",
    pendingActions: "Actions waiting for synchronization",
    pendingCheckins: "{{n}} check-in(s) pending", pendingLeads: "{{n}} lead(s) pending",
    synced: "Synchronization complete", noEvent: "No activation in progress.",
    noEventHint: "Check in or pick an event from My QR.",
    chooseEvent: "Pick today's activation:",
    syncBlocked: "{{n}} lead(s) could not be handed off",
    stuckLeads: "A lead is waiting for an event — open My QR to complete it.",
  },
  daySummary: {
    title: "End-of-day summary", leaders: "Team leader(s)",
    live: "Activation still running — figures keep moving.",
    cancelled: "This event is cancelled.",
    attendance: "Attendance", registered: "Registered", present: "Present", noShow: "No-show",
    rate: "Attendance rate", missions: "Missions", missionsTotal: "Missions planned",
    pendingReview: "Pending review", completion: "Completion rate",
    completionHint: "Approved / expected", leads: "Leads",
    points: "Points awarded", pointsTotal: "Total", pointsManual: "Manual adjustments",
    partnerReport: "Partner report", participants: "Participants",
    reviewQueue: "Review queue", generated: "Generated on",
    notCounted: "not counted", notConfirmed: "not confirmed",
  },
  review: {
    queue: "Review queue", needsReview: "Needs review", approve: "Approve", reject: "Reject",
    rejectReason: "Rejection reason", empty: "Nothing to review — all caught up",
    attendance: "Attendance", markPresent: "Mark present", markNoShow: "Mark no-show",
    removePhoto: "Remove photo", reviewed: "Reviewed",
  },
  admin: {
    invite: "Invite", inviteUser: "Invite a user", role: "Role", name: "Name", team: "Team",
    newEvent: "New event", newCampaign: "New campaign", newTeam: "New team",
    newPartner: "New partner", newMission: "New mission", attendancePoints: "Attendance points",
    missionPoints: "Points per mission", retention: "Image retention (days)",
    adjustScore: "Adjust score", inviteCreated: "Invitation created", inviteLink: "Invitation link",
    super_admin: "Super admin", admin: "Admin", team_leader: "Team leader", ambassador: "Ambassador",
    globalConfig: "Point configuration", manualAdjust: "Manual adjustment",
    socialImage: "Social image — 1200 × 630 URL",
  },
  profile: {
    title: "Profile", myScore: "My score", history: "History", prefsEmail: "Receive emails",
    ledger: "Point ledger", noShows: "No-shows",
  },
  notif: { title: "Notifications", empty: "No notifications", markAll: "Mark all as read" },

  pub: {
    nav: { home: "Home", events: "Events", partners: "Partners", about: "About", login: "Login" },
    eyebrow: "People • Partners • Community • Growth",
    heroTitle: "Live activations that create movement.",
    heroText: "VytaliDays brings Ambassadors, partners, businesses and communities together through real-world experiences designed to create visibility, engagement, new relationships and growth opportunities.",
    ctaEvents: "Explore upcoming VytaliDays",
    ctaPartner: "Become a partner",
    ctaPartnerVytali: "Become a Vytali partner",
    strategic: "VytaliDays transforms real-world events into opportunities for people, businesses and communities to grow together.",
    whatTitle: "More than an event.",
    whatLead: "VytaliDays creates value before, during and after every activation.",
    pillars: {
      ambassadors: { title: "Ambassadors", text: "People mobilized to create interactions, represent campaigns and bring the experience to life in the field." },
      partners: { title: "Partners", text: "Businesses and entrepreneurs who benefit from visibility, activations, content and new opportunities." },
      community: { title: "Community", text: "Participants and members discovering new people, businesses and experiences across the Vytali ecosystem." },
      growth: { title: "Growth", text: "Every activation can help create new members, leads, business relationships, content and long-term engagement." },
    },
    phasesTitle: "An activation that starts before the event.",
    phases: {
      before: { label: "Before", items: ["partner business promotion", "content and social posts", "invitations", "Ambassador mobilization", "campaign preparation", "pre-event visibility"] },
      during: { label: "During", items: ["Ambassador presence", "participant interactions", "partner activation", "content creation", "lead generation", "registrations", "community experience"] },
      after: { label: "After", items: ["opportunity follow-up", "commercial conversion", "post-event content", "results analysis", "partner relationship development", "preparation for future activations"] },
    },
    upcomingTitle: "Upcoming VytaliDays",
    upcomingLead: "Discover the activations coming to your area.",
    viewEvent: "View event",
    noEvents: "New VytaliDays are coming soon.",
    allEvents: "All VytaliDays",
    pastEvents: "Past activations",
    partnerTitle: "Put your business at the heart of a future VytaliDay.",
    partnerText: "VytaliDays gives partner businesses and entrepreneurs access to a complete activation around their business: pre-event visibility, field presence, content, interactions, opportunity generation and post-event follow-up.",
    partnerBenefits: ["Local visibility", "Pre-event promotion", "Ambassador activation", "Photo and video content", "Lead generation", "Vytali community exposure", "Customer engagement", "Results measurement"],
    retention: "One partnership. Multiple activations. Continuous visibility.",
    businessTypes: { restaurant: "Restaurant", cafe: "Café", retail: "Retail", gym: "Gym / sports", beauty: "Beauty / wellness", services: "Services", other: "Other" },
    formTitle: "Let's talk about your next activation",
    formLead: "Fill in this form and the Vytali team will get back to you.",
    form: {
      firstName: "First name", lastName: "Last name", business: "Business name", email: "Email",
      phone: "Phone", city: "City", businessType: "Business type",
      language: "Preferred language", message: "Message",
      goal: "What would you like to grow or improve?",
      consent: "I agree to be contacted by Vytali about my request.",
      send: "Send my request",
      success: "Thank you! Your request has been received. The Vytali team will contact you soon.",
      error: "Submission failed. Please try again.",
      requiredField: "This field is required",
      invalidEmail: "Invalid email",
      consentRequired: "Your consent is required",
    },
    ambassadorTitle: "Ambassadors bring VytaliDays to life.",
    ambassadorText: "Ambassadors are the human activation layer of every event: they create the interactions, carry the campaigns and turn an event into an experience.",
    ambassadorPoints: ["Field experience", "Real missions", "Personal development", "Training", "Networking", "Progression", "Lead generation", "Community participation"],
    ambassadorCta: "Ambassador Login",
    ambassadorNote: "Ambassador access is invite-only.",
    communityTitle: "A community that grows with every event.",
    communityText: "Every VytaliDay creates new encounters and connections. Over time, these interactions help build a Vytali community made up of members, Ambassadors, partners and entrepreneurs who can create value for one another.",
    communityTags: ["Members", "Participants", "Ambassadors", "Partner businesses", "Partner employees", "Entrepreneurs"],
    activationTitle: "Interactions that become opportunities.",
    activationText: "Every activation generates interactions, registrations, qualified opportunities, referrals and partner engagement.",
    flixTitle: "Every partner has a growth story.",
    flixLine: "VytaliFlix captures the vision. VytaliDays puts the vision into motion.",
    flixText: "VytaliFlix can document where a partner is today, what the entrepreneur wants to change, what they hope to achieve over the next six months and what expectations they hold. VytaliDays then becomes one of the tangible activation moments that starts moving that vision forward. Over time, Vytali can document the partner's evolution.",
    flixCta: "Discover VytaliFlix",
    flixSoon: "VytaliFlix — coming soon",
    finalTitle: "Ready to be part of the next movement?",
    aboutTitle: "About VytaliDays",
    aboutLead: "VytaliDays is Vytali's live activation platform: real-world experiences that bring people, businesses and communities together.",
    aboutHow: "How it works",
    aboutSteps: [
      { title: "A campaign", text: "Vytali defines an activation campaign with its objectives and partner business." },
      { title: "A VytaliDay", text: "A field event is planned: location, schedule, agenda, missions and assigned Ambassadors." },
      { title: "The activation", text: "Ambassadors create interactions, content and opportunities on site." },
      { title: "The follow-up", text: "Results are measured and feed the partner relationship and future activations." },
    ],
    footerTagline: "A Vytali experience",
    footerPrivacy: "Privacy", footerTerms: "Terms", footerContact: "Contact",
    shareOn: "Share on",
  },
};

export default en;
